import { useCallback, useEffect, useRef, useState } from 'react'
import { sha256Hex } from '@/shared/lib/hash'
import {
  AlertTriangle, CheckCircle2, Download, FileSpreadsheet, Loader2, Play, Upload, XCircle,
} from 'lucide-react'
import { ErpTable, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, Note } from '@/shared/ui/states'
import { Field } from '@/shared/ui/Modal'
import { explain } from '@/shared/api/problem'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { fmtDateTime, fmtNumber } from '@/shared/lib/format'
import { cn } from '@/lib/utils'
import {
  applyImport, cancelImport, confirmUpload, downloadImportTemplate, importJob,
  importTemplateSpec, putToPresignedUrl, reserveUpload, startBoundaryImport,
  startImport, startStatisticsImport,
} from './api'
import {
  IMPORT_JOB_ACTIVE, UNIT_TYPE_LABEL,
  type ImportJobResponse, type UnitTypeCode,
} from '@/shared/api/types'

const IMPORTABLE: UnitTypeCode[] = ['COUNTY', 'CONSTITUENCY', 'WARD', 'POLLING_STATION']
/** Stations are points — the backend rejects a boundary on one. */
const BOUNDARY_TYPES: UnitTypeCode[] = ['COUNTY', 'CONSTITUENCY', 'WARD']

type ImportKind = 'UNITS' | 'STATISTICS' | 'BOUNDARIES'

const KINDS: { id: ImportKind; label: string; blurb: string }[] = [
  { id: 'UNITS', label: 'Units',
    blurb: 'The hierarchy itself — counties, constituencies, wards, polling stations.' },
  { id: 'STATISTICS', label: 'Statistics',
    blurb: 'Register and turnout figures per unit per election year. This is what fills the turnout column.' },
  { id: 'BOUNDARIES', label: 'Boundaries',
    blurb: 'One GeoJSON FeatureCollection of polygons, matched to units by a code property.' },
]

/** Storage purpose and accepted MIME per kind — the allowlist is enforced server-side. */
const UPLOAD: Record<ImportKind, { purpose: string; contentType: string; accept: string; hint: string }> = {
  UNITS: { purpose: 'IMPORT', contentType: 'text/csv', accept: '.csv,text/csv', hint: 'CSV, up to 100 MB.' },
  STATISTICS: { purpose: 'IMPORT', contentType: 'text/csv', accept: '.csv,text/csv', hint: 'CSV, up to 100 MB.' },
  BOUNDARIES: {
    purpose: 'BOUNDARY',
    contentType: 'application/geo+json',
    accept: '.json,.geojson,application/json,application/geo+json',
    hint: 'GeoJSON FeatureCollection, up to 150 MB. Parsed one feature at a time, so a national ward file is fine.',
  },
}

/**
 * The import wizard.
 *
 * Four steps, and the order matters: the dry run is not optional. Applying a
 * 46,000-row file without seeing the validation report first is how a hierarchy
 * gets half-loaded with the parent codes off by one.
 *
 *   1. Upload   → reserve a file through storage, PUT to the presigned URL, confirm
 *   2. Validate → POST the import { dryRun: true } → 202 + job id
 *   3. Review   → poll the job, read the report
 *   4. Apply    → POST /import/{id}/apply → 202, poll to completion
 *
 * Three kinds of import share that machinery and the same job table, so they share
 * this screen rather than growing three near-identical wizards:
 *
 * - **Units** — the hierarchy. Must go top-down: a constituency's parent code has
 *   to resolve, so counties come first. The type picker says so rather than letting
 *   the failure arrive as 290 identical "parent not found" rows.
 * - **Statistics** — register and turnout per unit per year. Without this the
 *   turnout column can never be anything but "—".
 * - **Boundaries** — one FeatureCollection for a whole level. The single-unit
 *   upload on a unit's Boundary tab is fine for a correction and unusable for the
 *   1,450 wards.
 */
export function ImportWizard() {
  const canImport = useCan('geography:import')

  const [kind, setKind] = useState<ImportKind>('UNITS')
  const [file, setFile] = useState<File | null>(null)
  const [unitType, setUnitType] = useState<UnitTypeCode>('COUNTY')
  const [codeProperty, setCodeProperty] = useState('code')
  const [fileId, setFileId] = useState<string | null>(null)
  const [job, setJob] = useState<ImportJobResponse | null>(null)
  const [busy, setBusy] = useState<null | 'upload' | 'validate' | 'apply' | 'cancel' | 'template'>(null)
  const [error, setError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  // ── Polling ───────────────────────────────────────────────────────────────
  // The job runs on a server-side poller with a 5s tick, so a 1.5s client poll
  // is responsive without hammering. Stops the moment the job leaves an active
  // state — no interval left running behind a closed tab.
  const active = job !== null && IMPORT_JOB_ACTIVE.includes(job.status)

  const refresh = useCallback(async (id: string) => {
    try {
      setJob(await importJob(id))
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => {
    if (!job || !active) return
    const t = setInterval(() => { void refresh(job.id) }, 1500)
    return () => clearInterval(t)
  }, [job, active, refresh])

  function resetAll() {
    setFile(null); setFileId(null); setJob(null); setError(null)
    if (fileRef.current) fileRef.current.value = ''
  }

  function switchKind(next: ImportKind) {
    // A half-finished units import has nothing to do with a boundary file, and
    // carrying the uploaded fileId across would validate the wrong thing.
    setKind(next)
    resetAll()
    if (next === 'BOUNDARIES' && !BOUNDARY_TYPES.includes(unitType)) setUnitType('WARD')
  }

  async function getTemplate() {
    setBusy('template'); setError(null)
    try {
      await downloadImportTemplate(
        kind === 'STATISTICS' ? 'STATISTICS' : 'UNITS',
        kind === 'STATISTICS' ? undefined : unitType,
      )
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(null)
    }
  }

  async function upload() {
    if (!file) return
    setBusy('upload'); setError(null)
    try {
      const reserved = await reserveUpload({
        filename: file.name,
        // Never `file.type`: some browsers report an empty type for .csv, Windows
        // sometimes reports application/vnd.ms-excel, and the storage allowlist is
        // exact. Send what the purpose accepts.
        contentType: UPLOAD[kind].contentType,
        sizeBytes: file.size,
        purpose: UPLOAD[kind].purpose,
      })
      await putToPresignedUrl(reserved.uploadUrl, file)
      // Hash the SAME File object that was PUT — the checksum is only worth something if
      // it describes the bytes that actually went up.
      await confirmUpload(reserved.fileId, await sha256Hex(file))
      setFileId(reserved.fileId)
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(null)
    }
  }

  async function validate() {
    if (!fileId) return
    setBusy('validate'); setError(null)
    try {
      const started = kind === 'BOUNDARIES'
        ? await startBoundaryImport({
            fileId, unitType: unitType as 'COUNTY' | 'CONSTITUENCY' | 'WARD',
            codeProperty: codeProperty.trim() || 'code', dryRun: true,
          })
        : kind === 'STATISTICS'
          ? await startStatisticsImport({ fileId, unitType, dryRun: true })
          : await startImport({ fileId, unitType, dryRun: true })
      setJob(started)
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(null)
    }
  }

  async function apply() {
    if (!job) return
    setBusy('apply'); setError(null)
    try {
      await applyImport(job.id)
      await refresh(job.id)
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(null)
    }
  }

  async function cancel() {
    if (!job) return
    setBusy('cancel')
    try {
      await cancelImport(job.id)
      await refresh(job.id)
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(null)
    }
  }

  if (!canImport) return <Forbidden what="the unit import" />

  const step = job ? (job.status === 'VALIDATED' || !job.dryRun ? 3 : 2) : fileId ? 1 : 0
  const report = job?.report
  const canApply = job?.status === 'VALIDATED' && (report?.valid ?? 0) > 0

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Import"
        description="Units, statistics and boundaries from a bulk file. Nothing is written until you apply."
        right={job ? <button className="erp-btn" onClick={resetAll}>Start over</button> : undefined}
      />

      <ErrorStrip message={error} />

      {/* Kind first: it decides the file format, the storage purpose and the
          endpoint, so choosing it after picking a file would invalidate the file. */}
      <div className="flex items-center gap-1 border-b border-border overflow-x-auto">
        {KINDS.map((k) => (
          <button
            key={k.id}
            onClick={() => switchKind(k.id)}
            className={cn(
              'h-9 px-4 text-erp-sm border-b-2 -mb-px whitespace-nowrap',
              kind === k.id
                ? 'border-chocolate text-chocolate font-semibold'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {k.label}
          </button>
        ))}
      </div>
      <p className="text-erp-xs text-muted-foreground">
        {KINDS.find((k) => k.id === kind)?.blurb}
      </p>

      <Steps current={step} />

      {/* ── 1. File ─────────────────────────────────────────────────────── */}
      <Panel title="1 · Choose a file" description={UPLOAD[kind].hint}>
        <div className="space-y-3">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Unit type"
              hint={kind === 'UNITS'
                ? "Import top-down — counties before constituencies, and so on. A child's parent code must already exist."
                : kind === 'BOUNDARIES'
                  ? 'Which level the polygons in the file describe. Stations are points and have no boundary.'
                  : 'Which level the statistics rows resolve against.'}
            >
              <select
                value={unitType}
                onChange={(e) => setUnitType(e.target.value as UnitTypeCode)}
                className="erp-input w-full"
                disabled={Boolean(fileId)}
              >
                {(kind === 'BOUNDARIES' ? BOUNDARY_TYPES : IMPORTABLE).map((t) => (
                  <option key={t} value={t}>{UNIT_TYPE_LABEL[t] ?? t}</option>
                ))}
              </select>
            </Field>
            <Field label="File" required>
              <input
                ref={fileRef}
                type="file"
                accept={UPLOAD[kind].accept}
                disabled={Boolean(fileId)}
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="erp-input w-full !h-auto py-1.5 file:mr-3 file:border-0 file:bg-muted file:px-3 file:py-1 file:rounded file:text-erp-xs"
              />
            </Field>
          </div>

          {kind === 'BOUNDARIES' && (
            <Field
              label="Code property"
              required
              hint="The GeoJSON feature property holding the unit code. IEBC files call it code, ward_code, WARDCODE or CODE depending on the source — the dry run tells you which properties each feature actually has."
            >
              <input
                value={codeProperty}
                onChange={(e) => setCodeProperty(e.target.value)}
                disabled={Boolean(fileId)}
                className="erp-input w-full sm:w-64 font-mono"
              />
            </Field>
          )}

          {file && (
            <p className="text-erp-xs text-muted-foreground">
              <FileSpreadsheet className="h-3 w-3 inline mr-1" />
              {file.name} · {fmtNumber(Math.round(file.size / 1024))} KB
            </p>
          )}

          {/* A GeoJSON file has no template to hand out; the other two kinds do. */}
          {kind !== 'BOUNDARIES' && (
            <div className="flex items-center gap-2 flex-wrap">
              <button className="erp-btn" onClick={getTemplate} disabled={busy !== null}>
                {busy === 'template'
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Download className="h-3.5 w-3.5" />}
                Download {kind === 'STATISTICS'
                  ? 'statistics'
                  : (UNIT_TYPE_LABEL[unitType] ?? unitType).toLowerCase()} template
              </button>
              <span className="text-erp-xs text-muted-foreground">
                A filled CSV with the exact header the parser expects. Fill it in and upload it back.
              </span>
            </div>
          )}

          <ColumnReference kind={kind} unitType={unitType} />

          {fileId ? (
            <Note tone="good">File uploaded. Run the validation pass next.</Note>
          ) : (
            <button className="erp-btn erp-btn-primary" onClick={upload} disabled={!file || busy !== null}>
              {busy === 'upload' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              Upload
            </button>
          )}
        </div>
      </Panel>

      {/* ── 2. Validate ─────────────────────────────────────────────────── */}
      {fileId && (
        <Panel
          title="2 · Validate"
          description="A dry run. Parses every row, resolves parent codes and checks coordinates — writes nothing."
        >
          {!job ? (
            <button className="erp-btn erp-btn-primary" onClick={validate} disabled={busy !== null}>
              {busy === 'validate' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
              Run validation
            </button>
          ) : (
            <JobProgress job={job} onCancel={cancel} cancelling={busy === 'cancel'} />
          )}
        </Panel>
      )}

      {/* ── 3. Report ───────────────────────────────────────────────────── */}
      {report && (
        <Panel
          title="3 · Validation report"
          description={job?.dryRun ? 'Nothing has been written yet.' : 'Applied.'}
          bodyClassName="p-0"
        >
          <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-border border-b border-border">
            <Stat label={kind === 'BOUNDARIES' ? 'Features' : 'Rows'} value={fmtNumber(report.totalRows)} />
            <Stat label="Valid" value={fmtNumber(report.valid)} tone={report.valid > 0 ? 'good' : undefined} />
            <Stat
              label="Invalid"
              value={fmtNumber(report.invalidCount)}
              tone={report.invalidCount > 0 ? 'bad' : undefined}
            />
            <Stat
              label={kind === 'BOUNDARIES' ? 'Matched' : 'New / updated'}
              value={kind === 'BOUNDARIES'
                ? fmtNumber(report.wouldUpdate)
                : `${fmtNumber(report.wouldCreate)} / ${fmtNumber(report.wouldUpdate)}`}
            />
          </div>

          {/*
            Warnings are not errors and must not read as errors. A ward that is 99.7%
            inside its constituency is correct data — boundary files from different
            sources and years disagree at the edges. A ward in the WRONG COUNTY is a
            code-property mistake, and that is what this list is for.
          */}
          {(report.warnings?.length ?? 0) > 0 && (
            <div className="px-4 py-3 border-b border-border space-y-1.5">
              <p className="text-erp-xs font-medium">
                {fmtNumber(report.warnings?.length ?? 0)} warning(s) — not blocking
              </p>
              <ul className="text-erp-xs text-muted-foreground space-y-0.5 max-h-40 overflow-y-auto">
                {report.warnings?.map((w, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0 text-amber-600" />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {report.invalid.length > 0 ? (
            <>
              <ErpTable head={<><Th>Row</Th><Th>Column</Th><Th>Problem</Th></>}>
                {report.invalid.map((r, i) => (
                  <tr key={`${r.row}-${i}`}>
                    <Td className="erp-metric">{r.row ?? '—'}</Td>
                    <Td className="font-mono text-erp-xs">{r.column ?? '—'}</Td>
                    <Td>{r.message ?? '—'}</Td>
                  </tr>
                ))}
              </ErpTable>
              {report.invalidCount > report.invalid.length && (
                <p className="px-4 py-2 text-erp-xs text-muted-foreground border-t border-border">
                  Showing the first {report.invalid.length} of {fmtNumber(report.invalidCount)} problems —
                  the server caps the list. Fix these and re-run; the rest are likely the same cause.
                </p>
              )}
            </>
          ) : (
            <div className="px-4 py-4">
              <Note tone="good">Every row parsed cleanly.</Note>
            </div>
          )}
        </Panel>
      )}

      {/* ── 4. Apply ────────────────────────────────────────────────────── */}
      {job && job.dryRun && (
        <Panel
          title="4 · Apply"
          description="Writes the valid rows. Idempotent on (type, code) — re-importing updates rather than duplicating."
        >
          <div className="space-y-3">
            {(report?.invalidCount ?? 0) > 0 && (
              <Note tone="bad">
                <span className="font-medium">{fmtNumber(report?.invalidCount ?? 0)} rows will be skipped.</span>{' '}
                Applying now imports only the valid ones. Fix the file and re-validate if you
                want the whole set.
              </Note>
            )}
            <button className="erp-btn erp-btn-primary" onClick={apply} disabled={!canApply || busy !== null}>
              {busy === 'apply' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
              Apply {fmtNumber(report?.valid ?? 0)} rows
            </button>
            {!canApply && job.status !== 'VALIDATED' && (
              <p className="text-erp-xs text-muted-foreground">
                Available once validation finishes.
              </p>
            )}
          </div>
        </Panel>
      )}

      {job && !job.dryRun && job.status === 'COMPLETED' && (
        <Note tone="good">
          {kind === 'BOUNDARIES'
            ? `Applied ${fmtNumber(job.updatedCount)} boundaries. Each unit's centroid and area were recomputed from its polygon.`
            : kind === 'STATISTICS'
              ? `Recorded ${fmtNumber(job.createdCount + job.updatedCount)} statistic rows. Turnout has been recomputed up the tree.`
              : `Imported ${fmtNumber(job.createdCount)} new and updated ${fmtNumber(job.updatedCount)} existing units. Parent rollups have been recomputed.`}
        </Note>
      )}
    </div>
  )
}

function Steps({ current }: { current: number }) {
  const labels = ['Choose a file', 'Validate', 'Review', 'Apply']
  return (
    <ol className="flex items-center gap-2 overflow-x-auto">
      {labels.map((l, i) => (
        <li key={l} className="flex items-center gap-2 shrink-0">
          <span className={cn(
            'h-5 w-5 rounded-full inline-flex items-center justify-center text-erp-xs font-semibold',
            i < current ? 'bg-chocolate text-chocolate-foreground'
              : i === current ? 'border-2 border-chocolate text-chocolate'
              : 'border border-border text-muted-foreground',
          )}>
            {i < current ? '✓' : i + 1}
          </span>
          <span className={cn(
            'text-erp-sm',
            i === current ? 'font-medium' : 'text-muted-foreground',
          )}>
            {l}
          </span>
          {i < labels.length - 1 && <span className="w-6 h-px bg-border" />}
        </li>
      ))}
    </ol>
  )
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'good' | 'bad' }) {
  return (
    <div className="px-4 py-3 min-w-0">
      <div className="text-erp-xs uppercase tracking-wide text-muted-foreground truncate">{label}</div>
      <div className={cn(
        'text-erp-xl font-semibold erp-metric',
        tone === 'good' && 'text-emerald-700 dark:text-emerald-400',
        tone === 'bad' && 'text-red-700 dark:text-red-400',
      )}>
        {value}
      </div>
    </div>
  )
}

function JobProgress({
  job, onCancel, cancelling,
}: { job: ImportJobResponse; onCancel: () => void; cancelling: boolean }) {
  const active = IMPORT_JOB_ACTIVE.includes(job.status)
  const total = job.totalRows ?? 0
  const pct = total > 0 ? Math.min(100, Math.round((job.processedRows / total) * 100)) : null

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-erp-sm">
        {active ? <Loader2 className="h-3.5 w-3.5 animate-spin text-chocolate" />
          : job.status === 'COMPLETED' || job.status === 'VALIDATED'
            ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
            : <XCircle className="h-3.5 w-3.5 text-red-600" />}
        <span className="font-medium capitalize">{job.status.toLowerCase()}</span>
        {total > 0 && (
          <span className="text-muted-foreground erp-metric">
            {fmtNumber(job.processedRows)} / {fmtNumber(total)}
          </span>
        )}
        {active && (
          <button className="erp-btn ml-auto" onClick={onCancel} disabled={cancelling}>
            {cancelling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Cancel'}
          </button>
        )}
      </div>

      {pct !== null && (
        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
          <div className="h-full bg-chocolate transition-all" style={{ width: `${pct}%` }} />
        </div>
      )}

      {job.error && (
        <Note tone="bad">
          <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />
          {job.error}
        </Note>
      )}

      <p className="text-erp-xs text-muted-foreground">
        Job {job.id.split('-')[0]} · started {fmtDateTime(job.startedAt)}
        {job.finishedAt ? ` · finished ${fmtDateTime(job.finishedAt)}` : ''}
      </p>
    </div>
  )
}

/**
 * The column reference, served.
 *
 * `GET /geography/import/template/columns` and the downloadable CSV are generated
 * from ONE `ImportTemplates` class server-side, so what is rendered here cannot
 * drift from what the parser accepts. This replaced a hand-maintained copy of the
 * header row, which is exactly the kind of duplication that goes stale silently.
 *
 * Both CSV kinds have a served spec. Boundaries do not — a GeoJSON FeatureCollection
 * has no header row to describe — so that one keeps a short static description, and
 * is presented as what it is rather than dressed up as the same thing.
 */
function ColumnReference({ kind, unitType }: { kind: ImportKind; unitType: UnitTypeCode }) {
  if (kind === 'BOUNDARIES') return <BoundaryColumns />
  return <CsvColumns kind={kind} unitType={unitType} />
}

function CsvColumns({ kind, unitType }: { kind: ImportKind; unitType: UnitTypeCode }) {
  const isStats = kind === 'STATISTICS'
  const spec = useLoad(
    () => importTemplateSpec(isStats ? 'STATISTICS' : 'UNITS', isStats ? undefined : unitType),
    [kind, unitType],
  )

  return (
    <details className="border border-border rounded-md">
      <summary className="px-3 py-2 text-erp-sm cursor-pointer select-none">
        Expected columns
      </summary>
      <div className="px-3 pb-3 space-y-2">
        {spec.loading && !spec.data ? (
          <p className="text-erp-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin inline mr-1" /> Loading the column spec…
          </p>
        ) : spec.error || !spec.data ? (
          <Note tone="bad">
            Could not load the column reference ({spec.error ?? 'no data'}). Download the
            template instead — it carries the same header.
          </Note>
        ) : (
          <>
            <pre className="text-erp-xs font-mono bg-muted/40 border border-border rounded-md p-2 overflow-x-auto">
              {spec.data.columns.map((c) => c.name).join(',')}
            </pre>
            <ErpTable head={<><Th>Column</Th><Th>Type</Th><Th>Required</Th><Th>Meaning</Th><Th className="axis-hide-sm">Example</Th></>}>
              {spec.data.columns.map((c) => (
                <tr key={c.name}>
                  <Td className="font-mono text-erp-xs">{c.name}</Td>
                  <Td className="text-erp-xs">{c.type}</Td>
                  <Td className="text-erp-xs">{c.required ? 'Yes' : 'No'}</Td>
                  <Td className="text-erp-xs">{c.description}</Td>
                  <Td className="font-mono text-erp-xs axis-hide-sm">{c.example ?? '—'}</Td>
                </tr>
              ))}
            </ErpTable>
            <ul className="text-erp-xs text-muted-foreground space-y-1">
              {spec.data.notes.map((n) => <li key={n}>{n}</li>)}
              {/*
                Not in the served notes because it is a client-side observation about
                spreadsheet software, not a property of the format.
              */}
              <li>
                Column order does not matter; header names do. Case is ignored, but spaces
                inside a name are not — write <span className="font-mono">registeredVoters</span>,
                not “Registered Voters”.
              </li>
            </ul>
          </>
        )}
      </div>
    </details>
  )
}

function BoundaryColumns() {
  return (
    <details className="border border-border rounded-md">
      <summary className="px-3 py-2 text-erp-sm cursor-pointer select-none">
        What the file must contain
      </summary>
      <div className="px-3 pb-3">
        <ul className="text-erp-xs text-muted-foreground space-y-1">
          <li>
            A GeoJSON <span className="font-mono">FeatureCollection</span>. Each feature's
            geometry must be a <span className="font-mono">Polygon</span> or{' '}
            <span className="font-mono">MultiPolygon</span>, in WGS84 (EPSG:4326).
          </li>
          <li>
            Every feature needs the code property named above. A feature whose code does not
            match a unit fails on its own — the rest of the file still applies.
          </li>
          <li>
            An invalid polygon is <strong>rejected, not repaired</strong>. Fix it at source
            (QGIS: Fix Geometries) rather than having the stored geometry differ from the
            one you checked.
          </li>
          <li>
            A boundary that falls outside its parent's is reported as a warning, not an
            error: sources genuinely disagree at the edges. A boundary in the wrong county,
            though, means the code property is wrong — which is what the dry run is for.
          </li>
          <li>
            Applying recomputes each unit's centroid and area from its polygon, and marks
            the pin as derived.
          </li>
        </ul>
      </div>
    </details>
  )
}
